import {
  DATE_TIME_SHORT_FORMAT,
  getDateFromSeconds,
  getDateInFormat
} from "@/helpers/date.helpers"

import { getConnectionStatus } from "./useAgentPlatform.selector"

import type {
  AgentPlatformKey,
  Deployment,
  DeploymentsResponse,
  FiltersDeployment,
  FiltersDeploymentResponse,
  SensorsCountResponse
} from "@/api/useDeployments.types"

type Key = keyof FiltersDeployment

export const getOptionsForFilters = (response: FiltersDeploymentResponse) => {
  const { data } = response || {}
  const keys = Object.keys(data) as Key[]

  return keys.reduce(
    (acc, key) => {
      acc[key] = data[key as Key].map((value) => ({
        label: value.indexOf(" ") !== -1 ? `"${value}"` : value,
        value
      }))

      return acc
    },
    {} as Record<Key, { label: string; value: string }[]>
  )
}

// The deployment types this page can show, with their labels; deployments of
// any other type (e.g. one added by newer sensors) are left out
const DEPLOYMENT_TYPE_LABELS = new Map([
  ["K8S", "Kubernetes"],
  ["ECS", "ECS"],
  ["EC2", "EC2"],
  ["VM", "VM"],
  ["LAMBDA", "AWS Lambda"],
  ["AZURE_APP", "Azure Apps"],
  ["AGENTCORE", "AgentCore"],
  ["FOUNDRY_AGENT", "Foundry"],
  ["AGENTCORE_CLOUD", "AgentCore (cloud connection)"],
  ["COPILOT_STUDIO", "Copilot Studio"]
])

// Cloud connections run no sensors: a connection status, and their settings
// open from the row. An AgentCore account's version is its Kodem stack's
// template version, to update from those settings; Copilot Studio has none.
const AGENTLESS_DEPLOYMENT_TYPES = new Map<string, AgentPlatformKey>([
  ["AGENTCORE_CLOUD", "agentcore"],
  ["COPILOT_STUDIO", "copilot-studio"]
])

const NO_VERSION = "—"

const getAgentlessFields = (item: Deployment) =>
  AGENTLESS_DEPLOYMENT_TYPES.has(item.deploymentType)
    ? {
        kortexVersion: item.kortexVersion
          ? `Template v${item.kortexVersion}`
          : NO_VERSION,
        updateAvailable: !!item.kortexVersion && item.updateAvailable,
        connectionStatus: getConnectionStatus(item.platformStatus),
        platform: AGENTLESS_DEPLOYMENT_TYPES.get(item.deploymentType)
      }
    : {}

// A cloud connection counts as one sensor, steadily
const CLOUD_CONNECTION_SENSORS = { value: 1, percentage: 0 }

const getAverageSensors = (item: Deployment) =>
  AGENTLESS_DEPLOYMENT_TYPES.has(item.deploymentType)
    ? CLOUD_CONNECTION_SENSORS
    : {
        value: Math.round(item.averageSensors || 0),
        percentage: Math.round(item.averageSensorsTrendPercentage || 0)
      }

export const getDeployments = (response: DeploymentsResponse) => {
  const { data, metadata } = response
  return {
    metadata,
    data: data.assets
      ?.filter((item) => DEPLOYMENT_TYPE_LABELS.has(item.deploymentType))
      .map((item) => ({
        ...item,
        ...getAgentlessFields(item),
        key: `${item.assetId}-${item.environmentName}`,
        status:
          item.status === "STATUS_CONNECTED" ? "Connected" : "Disconnected",

        deploymentType:
          DEPLOYMENT_TYPE_LABELS.get(item.deploymentType) ??
          item.deploymentType,
        firstInstallationTime: getDateInFormat(
          getDateFromSeconds(item.firstInstallationTime),
          DATE_TIME_SHORT_FORMAT
        ),
        lastUpgradeTime: getDateInFormat(
          getDateFromSeconds(item.lastUpgradeTime),
          DATE_TIME_SHORT_FORMAT
        ),
        lastHeartbeatTime: getDateInFormat(
          getDateFromSeconds(item.lastHeartbeatTime),
          DATE_TIME_SHORT_FORMAT
        ),
        avg: getAverageSensors(item)
      }))
  }
}

export const getSensorsCount = (response: SensorsCountResponse) => {
  const { data } = response || {}
  const {
    averageLiveCount = 0,
    previousAverageLiveCount = 0,
    trendPercentage = 0,
    buckets = [],
    averageBuckets = []
  } = data || {}

  return {
    buckets,
    averageBuckets,
    averageLiveCount: Math.round(averageLiveCount),
    previousAverageLiveCount: Math.round(previousAverageLiveCount),
    trendPercentage: Math.round(trendPercentage),
    highestPeakLiveCount: Math.round(
      buckets.length > 0
        ? Math.max(...buckets.map((bucket) => bucket.liveCount))
        : 0
    )
  }
}
